import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AddTitleInsuranceComponent } from '../../components/add-title-insurance/add-title-insurance.component';
import { FctCancelModalComponent } from '../components/fct-cancel-modal/fct-cancel-modal.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatterDispatch } from '@state/matter';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { BlankTitleInsurance } from '../../chicago/chicago.component';
import { Select } from '@ngxs/store';
import { combineLatest, Observable } from 'rxjs';
import {
	BusinessMatterClient,
	Matter,
	MatterTitle,
	PersonMatterClient,
} from '@models/matter';
import { PropertyUsage } from '@models/title-insurance';
import { Step } from 'src/app/shared/atomic/molecules/step-progress-nav/step-progress-nav.component';
import {
	MissingFieldSection,
	ValidateFctDataService,
} from '../services/validate-fct-data.service';
import { MatterMortgageWithAssociations } from '@models/mortgage';
import { MatterClients } from '@state/global/matter_clients';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { MatterFCTCommercialTitleInsurance } from '@models/title-insurance/fct';
import { ICON } from '@core/util/icon';
import { isSet } from '@core/util/value';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import { isRegionOntario } from '@core/util/address.utils';
import { TestHooks } from './commercial-fct.hooks';

const Steps: Step[] = [
	{ title: 'Collect information' },
	{ title: 'Complete form' },
	{ title: 'Submit to FCT' },
	{ title: 'Enter policy details' },
	{ title: 'Order summary' },
];

@UntilDestroy()
@Component({
	selector: 'app-commercial-fct',
	templateUrl: './commercial-fct.component.html',
	styleUrls: ['./commercial-fct.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CommercialFctComponent implements OnInit {
	testHooks = TestHooks;
	public blankTitleInsurance = BlankTitleInsurance;
	public titleInsurance: MatterFCTCommercialTitleInsurance;
	public usageType = PropertyUsage;
	public steps = Steps;
	public currentStep = 0;
	public missing: MissingFieldSection[] = [];
	public outOfOntario = false;
	public orderValid = false;
	public ICON = ICON;
	public missingFieldsMessage = '';
	public nextDisabled = false;

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(SelectedMatter.getTitleInsurance)
	titleInsurance$: Observable<MatterFCTCommercialTitleInsurance>;

	@Select(SelectedMatter.getSelected) matter$: Observable<Matter>;
	@Select(MatterMortgagesState.getAllMortgagesWithAssociations)
	mortgages$: Observable<MatterMortgageWithAssociations[]>;
	@Select(MatterClients.get_mortgagorsWithAssociationMapped)
	clients$: Observable<(PersonMatterClient | BusinessMatterClient)[]>;
	@Select(MatterTitlesState.getAll) matterTitles$: Observable<MatterTitle[]>;

	constructor(
		private dialog: MatDialog,
		private matterDispatch: MatterDispatch,
		private validateFctDataService: ValidateFctDataService,
	) {}

	ngOnInit(): void {
		this.titleInsurance$
			.pipe(untilDestroyed(this))
			.subscribe((titleInsurance) => {
				if (titleInsurance) {
					this.titleInsurance = titleInsurance;

					if (this.currentStep === 3 && !titleInsurance.policies?.length) {
						this.nextDisabled = true;
					} else {
						this.nextDisabled = false;
					}
				} else {
					this.nextDisabled = true;
				}
			});

		combineLatest([
			this.matter$,
			this.clients$,
			this.mortgages$,
			this.matterTitles$,
		])
			.pipe(untilDestroyed(this))
			.subscribe(([_matter, _clients, _mortgages, _matterTitles]) => {
				if (!_matter) {
					return;
				}

				this.outOfOntario = !isRegionOntario(
					_matter?.propertyDetails?.address?.province,
				);

				this.missing = this.validateFctDataService.getSubmitDataMissingSections(
					_matter,
					_clients,
					_mortgages,
					_matterTitles,
				);

				this.setMissingFieldsMessage();

				this.orderValid = this.missing?.length === 0 && !this.outOfOntario;

				if (_matter.titleInsurance) {
					const titleInsurance =
						_matter.titleInsurance as MatterFCTCommercialTitleInsurance;
					if (isSet(titleInsurance.currentStep)) {
						this.currentStep = titleInsurance.currentStep;
					}
				}
			});
	}

	selectInsuranceProvider() {
		this.dialog.open(AddTitleInsuranceComponent);
	}

	cancel(): void {
		const dialogRef = this.dialog.open(FctCancelModalComponent, {
			data: {
				isCommercial: true,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(untilDestroyed(this))
			.subscribe((result: boolean) => {
				if (result) {
					this.matterDispatch.SetTitleInsurance(
						this.matterId,
						this.blankTitleInsurance,
					);
				}
			});
	}

	setMissingFieldsMessage() {
		if (this.missing.length > 0 && [1, 2].includes(this.currentStep)) {
			this.missingFieldsMessage = `You have ${this.missing.length + 1} missing fields.`;
			this.steps[0].warningSubTitle = `${this.missing.length + 1} missing fields`;
		} else {
			this.missingFieldsMessage = '';
			this.steps[0].warningSubTitle = '';
		}
	}

	continue(): void {
		this.currentStep++;
		this.matterDispatch.SetTitleInsuranceCurrentStep(
			this.matterId,
			this.currentStep,
		);
		this.setMissingFieldsMessage();
	}

	goBack(): void {
		this.currentStep--;
		this.matterDispatch.SetTitleInsuranceCurrentStep(
			this.matterId,
			this.currentStep,
		);
		this.setMissingFieldsMessage();
	}

	stepClicked(step: number): void {
		if (step === 4) {
			return;
		}
		this.currentStep = step;
		this.matterDispatch.SetTitleInsuranceCurrentStep(
			this.matterId,
			this.currentStep,
		);
		this.setMissingFieldsMessage();
	}
}
